import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';

export interface AnomalyResult {
  id: string;
  organisation_id: string;
  connection_id: string;
  connection_type: string | null;
  check_id: string | null;
  check_name: string;
  check_type: string;
  metric: string;
  severity: string;
  status: string;
  anomaly_date: string;
  actual_value: number | null;
  expected_value: number | null;
  deviation_pct: number | null;
  details: Record<string, unknown> | null;
  error_message: string | null;
  created_at: string;
}

interface UseAnomalyResultsReturn {
  anomalies: AnomalyResult[];
  criticalCount: number;
  warningCount: number;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

export function useAnomalyResults(organisationId: string | undefined): UseAnomalyResultsReturn {
  const [anomalies, setAnomalies] = useState<AnomalyResult[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchAnomalies = useCallback(async () => {
    if (!organisationId) {
      setAnomalies([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      const cutoffDate = thirtyDaysAgo.toISOString().split('T')[0];

      const { data, error: queryError } = await (supabase
        .from('anomaly_results' as any)
        .select('*')
        .eq('organisation_id', organisationId)
        .eq('status', 'open')
        .gte('anomaly_date', cutoffDate)
        .order('anomaly_date', { ascending: false }) as any);

      if (queryError) {
        throw new Error(queryError.message);
      }

      // Sort: critical first, then warning, then info, then by date desc
      const severityOrder: Record<string, number> = { critical: 0, warning: 1, info: 2 };
      const rows = (data || []) as AnomalyResult[];
      const sorted = rows.sort((a, b) => {
        const sevDiff = (severityOrder[a.severity] ?? 3) - (severityOrder[b.severity] ?? 3);
        if (sevDiff !== 0) return sevDiff;
        return new Date(b.anomaly_date).getTime() - new Date(a.anomaly_date).getTime();
      });

      // Deduplicate: keep highest severity per metric+date+connection
      const deduped: AnomalyResult[] = [];
      const seen = new Set<string>();
      for (const a of sorted) {
        const key = `${a.metric}|${a.anomaly_date}|${a.connection_type}`;
        if (!seen.has(key)) {
          seen.add(key);
          deduped.push(a);
        }
      }

      setAnomalies(deduped);
    } catch (err) {
      console.error('Failed to fetch anomaly results:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch anomaly results');
    } finally {
      setLoading(false);
    }
  }, [organisationId]);

  useEffect(() => {
    fetchAnomalies();
  }, [fetchAnomalies]);

  const criticalCount = anomalies.filter(a => a.severity === 'critical').length;
  const warningCount = anomalies.filter(a => a.severity === 'warning').length;

  return { anomalies, criticalCount, warningCount, loading, error, refetch: fetchAnomalies };
}
